import type ExcelJS from 'exceljs';

// Parse an uploaded distributor price list / supplier quote (.xlsx). Maps columns
// by header name so vendors' varied layouts still import. Source defaults to
// distributor; a sheet with a "quote #" column is treated as a supplier quote.
async function xlsx(): Promise<typeof import('exceljs').default> {
  return (await import('exceljs')).default;
}

export interface PriceUploadRow {
  part_number: string; manufacturer: string; description: string;
  unit_price: number; supplier: string; source: string; quote_date: string; quote_number: string;
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
const HEADERS: Record<keyof PriceUploadRow, string[]> = {
  part_number: ['partnumber', 'partno', 'part', 'partnum', 'mfgpart', 'cat', 'catalog', 'sku', 'item'],
  manufacturer: ['manufacturer', 'mfg', 'mfr', 'brand', 'make'],
  description: ['description', 'desc', 'productdescription'],
  unit_price: ['unitprice', 'price', 'cost', 'netprice', 'yourprice', 'unitcost', 'sell'],
  supplier: ['supplier', 'distributor', 'vendor', 'seller'],
  source: ['source', 'type'],
  quote_date: ['quotedate', 'date', 'pricedate', 'effectivedate'],
  quote_number: ['quotenumber', 'quoteno', 'quote', 'quotenum'],
};

export async function parsePriceSheet(file: File, defaultSupplier = ''): Promise<PriceUploadRow[]> {
  const ExcelJS = await xlsx();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) throw new Error('No worksheet found.');

  // Find the header row (first row that has a part column and a price column).
  let headerRow = 0;
  const colMap: Partial<Record<keyof PriceUploadRow, number>> = {};
  for (let rn = 1; rn <= Math.min(ws.rowCount, 15); rn++) {
    const row = ws.getRow(rn);
    const found: Partial<Record<keyof PriceUploadRow, number>> = {};
    row.eachCell((cell, col) => {
      const h = norm(cellText(cell));
      for (const key of Object.keys(HEADERS) as (keyof PriceUploadRow)[]) {
        if (found[key] == null && HEADERS[key].includes(h)) found[key] = col;
      }
    });
    if (found.part_number != null && found.unit_price != null) { headerRow = rn; Object.assign(colMap, found); break; }
  }
  if (!headerRow) throw new Error('Could not find a header row with a part-number and price column.');

  const hasQuoteNo = colMap.quote_number != null;
  const out: PriceUploadRow[] = [];
  for (let rn = headerRow + 1; rn <= ws.rowCount; rn++) {
    const row = ws.getRow(rn);
    const get = (k: keyof PriceUploadRow) => colMap[k] ? cellText(row.getCell(colMap[k]!)) : '';
    const part = get('part_number');
    const priceText = colMap.unit_price ? cellNumberText(row.getCell(colMap.unit_price)) : '';
    const price = Number(priceText.replace(/[$,]/g, ''));
    if (!part || !Number.isFinite(price)) continue;
    out.push({
      part_number: part, manufacturer: get('manufacturer'), description: get('description'),
      unit_price: price, supplier: get('supplier') || defaultSupplier,
      source: (get('source') || (hasQuoteNo ? 'supplier_quote' : 'distributor')).toLowerCase().replace(/[^a-z_]/g, '') || 'distributor',
      quote_date: get('quote_date'), quote_number: get('quote_number'),
    });
  }
  if (!out.length) throw new Error('No priced rows found in the sheet.');
  return out;
}

function cellText(cell: ExcelJS.Cell): string {
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object') {
    if ('text' in v && typeof (v as { text?: unknown }).text === 'string') return (v as { text: string }).text.trim();
    if ('result' in v) return `${(v as { result?: unknown }).result ?? ''}`.trim();
    if ('richText' in v) return (v as { richText: { text: string }[] }).richText.map((t) => t.text).join('').trim();
    return '';
  }
  return `${v}`.trim();
}
function cellNumberText(cell: ExcelJS.Cell): string {
  const v = cell.value;
  if (typeof v === 'number') return `${v}`;
  if (v && typeof v === 'object' && 'result' in v) return `${(v as { result?: unknown }).result ?? ''}`;
  return `${v ?? ''}`.trim();
}
